/**
 * -----------------------------------------------------------------------------
 * Popup - Camera Video
 * -----------------------------------------------------------------------------
 * This popup is used for Camera Video
 */

import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { TCameraVideoProps } from "./type";
import { AdminFormFieldSubmit } from "../../components/admin/formFields";
import { useLoggedInUserData } from "../../utils/user";
import { ACCOUNT_RECORDER_TYPE_VALUES } from "../admin-details/account/constants";
import { CameraEventGetVideoDetailResponseVideos } from "../../api/types/Camera";
import AppVideoElement from "../camera-admin/components/videoElement";
import { APP_CONFIG } from "../../constants/constants";
import { PlaybackState } from "../camera-admin/components/storageVideoView";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../api/types/User";
import { asyncLoadScript } from "../../utils/common";
import { zPad } from "../../utils/number";
import { toast } from "react-toastify";

type TModalCameraPlayback = {
  cameraSerialNumber: string;
  videoAddress?: string;
  onClose?: () => void;
};

const debugComponent = true;

const ModalCameraPlayback: FC<TModalCameraPlayback> = ({
  cameraSerialNumber,
  videoAddress,
  onClose,
}) => {
  const { t } = useTranslation();

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
  const liveviewDurationSeconds =
    loggedInUser?.account?.cameraliveviewduration ?? 30;

  const initRef = React.useRef(false);
  const [progressValue, setProgressValue] = React.useState<number>(0);
  const [liveviewError, setLiveviewError] = React.useState<string | null>(null);
  const [sessionStable, setSessionStable] = React.useState(false);
  const playbackStateRef = React.useRef<PlaybackState>(PlaybackState.STOPPED);
  const setPlaybackState = (state: PlaybackState) => {
    playbackStateRef.current = state;
  };
  const playbackState = playbackStateRef.current;
  const [timerSeconds, setTimerSeconds] = useState<number | null>(null);
  const timerIntervalRef = React.useRef<any>(null);
  const [isFullScreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    clearTimerInterval();
  }, []);

  const clearTimerInterval = () => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log("== [clearTimerInterval] timer interval cleared");
    window.clearInterval(timerIntervalRef.current!);
  };

  const intervalCallback = React.useCallback(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("_");
    setTimerSeconds((time) => (time ?? 0) - 1);
  }, []);

  const initTimerInterval = (totalTime: number) => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log(
        "== [initTimerInterval] timer started",
        timerIntervalRef.current
      );
    setTimerSeconds(totalTime);
    timerIntervalRef.current = window.setInterval(intervalCallback, 1000);
  };

  const resetInterval = useCallback(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("== [resetInterval] timer reset");
    clearTimerInterval();
    setTimerSeconds(null);
  }, []);

  const resumeInterval = () => {
    if (APP_CONFIG.DEBUG.ALL) console.log("== [resumeInterval] timer resumed");
    timerIntervalRef.current = window.setInterval(intervalCallback, 1000);
  };

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const webRTCConfig = {
    // "staging" | "prod"
    env: loggedInUser?.config?.VISIONMAX_BASE_URL?.includes("staging")
      ? "staging"
      : "prod",
    // VM_ENV === "prods"
    //   ? process.env.REACT_APP_VISIONMAX_ENV
    //   : process.env.REACT_APP_VISIONMAX_ENV_STAGING,
    apiKey: loggedInUser?.config?.VISIONMAX_X_API_KEY,
    // VM_ENV === "prod"
    //   ? process.env.REACT_APP_VISIONMAX_KEY
    //   : process.env.REACT_APP_VISIONMAX_KEY_STAGING,
    clientAccessToken: localStorage.getItem(
      localStorageKeys.camerasAdminVMToken
    ),
    feature: {
      enableDefaultCameraSwitchButton: false,
    },
  };

  useEffect(() => {
    if (!sessionStable || progressValue !== 100) return;
    const vmRTC = (window as any).vmRTC;
    if (
      vmRTC &&
      modalsState.showModalVideoPlayback?.action === "play" &&
      playbackState === PlaybackState.STOPPED &&
      progressValue === 100 &&
      sessionStable
    ) {
      playClip();
      setLiveviewError(null);
    }
  }, [
    modalsState.showModalVideoPlayback?.captureMode,
    modalsState.showModalVideoPlayback?.duration,
    modalsState.showModalVideoPlayback?.startTimestamp,
    modalsState.showModalVideoPlayback?.action,
    sessionStable,
    progressValue,
  ]);

  const connectRTC = () => {
    if (initRef.current) return;

    // update the ref to ensure the code executes only once
    initRef.current = true;

    // initialize scripts start here
    if (APP_CONFIG.DEBUG.ALL) console.log("== [initLiveview]");

    // import the webRTC script from `public/js` directory
    // ---------------------------------------------------
    // other dependencies (aws-sdk-2.1490.0.min.js, mqttws31.js and adapter.js)
    // are already loaded in the index.html file
    // a copy of each of these files is archived in `src/components/visionmax-liveview-ui/archive/` directory
    asyncLoadScript(
      `${process.env.PUBLIC_URL}/js/vmRTC-umd.js`,
      "visionmax-liveview-webrtc",
      () => {
        if (APP_CONFIG.DEBUG.ALL)
          console.log("== [initLiveview] webrtc script loaded");
        try {
          // initialize the webRTC
          const vmRTC = (window as any).vmRTC;
          if (vmRTC) {
            // bind event handlers
            vmRTC.addEventListener("progressStatusUpdated", (input: any) => {
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [progressStatusUpdated]", input);
              setLiveviewError(null);
              setProgressValue(input);
            });
            //to catch event of connection fail
            vmRTC.addEventListener("connect fail", (input: any) => {
              vmRTC.stop();
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [connect fail]", input);
              setLiveviewError("connect fail");
              // onStopCallback?.();
            });
            vmRTC.addEventListener("session stable", (input: any) => {
              (window as any).liveviewSessionStable = true;
              if (!!(window as any)[`liveviewAttempt_${cameraSerialNumber}`]) {
                delete (window as any)[`liveviewAttempt_${cameraSerialNumber}`];
              }
              setSessionStable(true);
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [session stable]", input);
            });
            //to catch event of connection lost
            vmRTC.addEventListener("connection lost", () => {
              vmRTC.stop();
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [connection lost]");
              setLiveviewError("connection lost");
              // onStopCallback?.();
            });

            vmRTC.addEventListener("initial.fail", (input: any) => {
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [initial.fail]", input);
              setLiveviewError("initial fail");
              // onStopCallback?.();
            });

            vmRTC.addEventListener("broker disconnnected", (input: any) => {
              if (APP_CONFIG.DEBUG.ALL)
                console.log("== WEBRTCEVENT [broker disconnnected]", input);
              // connection was, probably, unsuccessful after a wakeup
              if (
                !(window as any).liveviewSessionStable &&
                (window as any)[`liveviewAttempt_${cameraSerialNumber}`] <
                  APP_CONFIG.CAMERA_ADMIN.LIVEVIEW_RETRIES
              ) {
                // onStopCallback?.();
              }
            });

            // setup and start
            if (APP_CONFIG.DEBUG.ALL || debugComponent)
              console.log("== [SERIAL_NUMBER]", cameraSerialNumber);
            vmRTC.setup(webRTCConfig).playback(cameraSerialNumber);
            (window as any)[`liveviewAttempt_${cameraSerialNumber}`] = !!(
              window as any
            )[`liveviewAttempt_${cameraSerialNumber}`]
              ? (window as any)[`liveviewAttempt_${cameraSerialNumber}`] + 1
              : 1;
          } else {
            console.error("== [initLiveview] vmRTC is not defined");
          }
        } catch (e) {
          if (APP_CONFIG.DEBUG.ALL) console.error("== ERR", e);
        }
      }
    );
  };

  const terminateRTC = useCallback(() => {
    const vmRTC = (window as any).vmRTC;
    if (vmRTC && typeof vmRTC.stop === "function") {
      try {
        vmRTC.stop(); // Stop the RTC connection
        initRef.current = false;
        console.log("[vmRTC] Connection stopped.");
      } catch (e) {
        console.error("[vmRTC] Error during stop:", e);
      }
    }
    // Reset everything related to the playback state
    resetInterval();
    setSessionStable(false);
    setProgressValue(0);
    setPlaybackState(PlaybackState.STOPPED);
    // onStopCallback?.(); // Call the onStop callback
  }, [dispatch, resetInterval]);

  const playClip = useCallback(() => {
    if (!modalsState.showModalVideoPlayback) return;
    const vmRTC = (window as any).vmRTC;
    if (!vmRTC) {
      console.error("vmRTC is not initialized");
      return;
    }
    // If the session is already stable, we can directly play the video clips
    const payload = {
      start: modalsState.showModalVideoPlayback.startTimestamp.toString(),
      end: (
        (modalsState.showModalVideoPlayback.startTimestamp ?? 0) +
        (modalsState.showModalVideoPlayback.duration ?? 0)
      ).toString(),
      capture_mode: modalsState.showModalVideoPlayback.captureMode,
      type: "SDCARD",
    };
    vmRTC.playVideoClips(payload, (res: any) => {
      if (res?.error) {
        // If no video is available, then error will be given
        toast.error(res?.error);
        hideModal();
        return;
      }
      setPlaybackState(PlaybackState.PLAYING);
      initTimerInterval(
        modalsState?.showModalVideoPlayback?.duration ??
          liveviewDurationSeconds
      );
      // onPlayCallback?.(res);
    });
  }, [initTimerInterval, modalsState.showModalVideoPlayback, liveviewDurationSeconds]);

  useEffect(() => {
    if (modalsState.showModalVideoPlayback && cameraSerialNumber) {
      if (modalsState.showModalVideoPlayback.action === "play") {
        if (!sessionStable) {
          // If the session is not stable, we need to connect RTC
          connectRTC();
        } else if (playbackState === PlaybackState.PAUSED) {
          // Resume playback if the action is play and the current state is paused
          const vmRTC = (window as any).vmRTC;
          if (vmRTC) {
            vmRTC.resumePlay({}, (res: any) => {
              if (APP_CONFIG.DEBUG.ALL) console.log("resumed");
            });
            setPlaybackState(PlaybackState.PLAYING);
            resumeInterval();
            // onPlayCallback?.({});
          }
        } else if (playbackState !== PlaybackState.PLAYING) {
          playClip();
          initTimerInterval(
            modalsState.showModalVideoPlayback.duration ??
              liveviewDurationSeconds
          );
        }
      } else if (modalsState.showModalVideoPlayback.action === "pause") {
        const vmRTC = (window as any).vmRTC;
        if (vmRTC) {
          vmRTC.pausePlay({}, (res: any) => {
            if (APP_CONFIG.DEBUG.ALL) console.log("paused successfully");
          });
          // onPauseCallback?.();
          clearTimerInterval();
          setPlaybackState(PlaybackState.PAUSED);
        }
      }
    } else {
      // if (sessionStable) {
      terminateRTC();
      // }
    }
  }, [cameraSerialNumber, modalsState.showModalVideoPlayback, playbackState, liveviewDurationSeconds]);

  useEffect(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("== [timerSeconds]", timerSeconds);
    if ((timerSeconds ?? 0) <= 0 && sessionStable) {
      // terminateRTC();
      dispatch(setModalsData({ ...modalsState, showModalVideoPlayback: null }));
    }
  }, [timerSeconds]);

  const hideModal = () => {
    terminateRTC();
    dispatch(setModalsData({ ...modalsState, showModalVideoPlayback: null }));
    if (onClose) onClose();
  };

  const TimerDisplayComponent = useCallback(() => {
    return (
      <div className="absolute right-4 top-4 font-bold text-white/70 text-lg z-40">
        {`${Math.floor((timerSeconds ?? 0) / 60)}:${zPad((timerSeconds ?? 0) % 60, 2)}`}
      </div>
    );
  }, [timerSeconds]);

  // Effect to observe full screen changes
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const handleFullscreen = () => {
    if (containerRef?.current) {
      if (isFullScreen) {
        document.exitFullscreen();
      } else {
        containerRef.current?.requestFullscreen();
      }
    }
  };

  if (modalsState?.showModalVideoPlayback === null) return null;

  return (
    <>
      <div
        className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
        onClick={hideModal}
      />
      <div
        className={
          "fixed inset-y-1/2 inset-x-1/2 w-[calc(100vw-6rem)] h-[calc(100vh-6rem)] bg-black/80 overflow-hidden " +
          "Xbg-white rounded-xl shadow-lg z-modal"
        }
        style={{ transform: "translate(-50%, -50%)" }}
        ref={containerRef}
        id="visionmax-playback"
      >
        {/* <div className="w-full h-full flex justify-center items-center relative"> */}
        <AdminFormFieldSubmit
          type="button"
          variant={"secondary"}
          buttonDimensionClass="absolute p-2 left-4 top-4 font-bold text-white/70 text-lg z-40"
          label={t("close")}
          onClick={hideModal}
        />
        {!!videoAddress && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 p-2 text-white font-medium pointer-events-none text-center">
            {videoAddress}
          </div>
        )}
        <TimerDisplayComponent />
        {/* Centered progress bar or error message display */}

        <>
          {!!liveviewError ? (
            <p className="absolute z-[2] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lg uppercase font-bold text-gray-200/70 text-center">
              {liveviewError}
            </p>
          ) : progressValue < 100 ? (
            <div className="absolute z-[2] top-0 left-0 w-full h-full flex items-center justify-center">
              <div
                className="w-[calc(100%-6rem)] border border-gray-400/70 rounded-md h-12 relative"
                role="progressbar"
                aria-label={`loading progress`}
                aria-valuemin={0}
                aria-valuenow={progressValue}
                aria-valuemax={100}
              >
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-200/70">
                  {progressValue}%
                </span>
                <div
                  className="absolute top-0 left-0 h-full bg-gray-400/25"
                  style={{ width: `${progressValue}%` }}
                ></div>
              </div>
            </div>
          ) : null}
        </>
        {/* Video element */}
        <video
          id="rtc_liveview"
          autoPlay
          controls={false}
          // fixedHeightClass={"h-full"}
          className={`absolute z-[1] top-0 left-0 w-full h-full`}
          disablePictureInPicture={true}
          playsInline
        />
        {/* Fullscreen Button (overlay) */}
        <button
          onClick={handleFullscreen}
          className="absolute bottom-4 right-4 px-3 py-2 bg-white/70 rounded-full shadow hover:bg-white z-10"
        >
          ⛶
        </button>
      </div>
    </>
  );
};
export default ModalCameraPlayback;
